import React, { useCallback, useEffect, useId, useState } from 'react';
import { FaEnvelope, FaHome, FaPhone } from 'react-icons/fa';
import Modal from '../Modal';
import { Button, Input, LabelPicker, Toggle } from '../../shared/ui';
import { API_URL } from '../../config/env';
import { setFamilyLabels, createLabel as apiCreateLabel } from '../../services/labels';
import styles from '../../pages/DirectoryPage.module.css';

function authHeaders(extra = {}) {
  return { Authorization: `Bearer ${localStorage.getItem('token')}`, ...extra };
}

function emptyFamilyForm() {
  return {
    case_number: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    is_active: true
  };
}

/**
 * Create/edit family — same fields as directory family modal.
 * onSaved(savedRow) after successful save. onLabelsRefresh() optional.
 */
export default function FamilyEditorModal({
  open,
  onClose,
  mode,
  record,
  labels,
  onLabelsRefresh,
  onSaved,
  flash,
  zIndex = 2000,
  closeOnBackdrop = true,
  closeOnEscape = true
}) {
  const formId = useId();
  const [familyForm, setFamilyForm] = useState(emptyFamilyForm());
  const [familyLabelIds, setFamilyLabelIds] = useState([]);
  const [familyError, setFamilyError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && record) {
      setFamilyForm({ ...emptyFamilyForm(), ...record });
      setFamilyLabelIds((record.labels || []).map((l) => l.id));
    } else {
      setFamilyForm(emptyFamilyForm());
      setFamilyLabelIds([]);
    }
    setFamilyError('');
  }, [open, mode, record]);

  const onFamilyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFamilyForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const onCreateLabelInline = useCallback(
    async (name) => {
      try {
        const created = await apiCreateLabel({ name, color: 'neutral', kind: 'both' });
        onLabelsRefresh?.();
        return created;
      } catch (e) {
        flash(e.message, 'error');
        return null;
      }
    },
    [flash, onLabelsRefresh]
  );

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFamilyError('');
    try {
      const isEdit = mode === 'edit';
      const url = isEdit ? `${API_URL}/api/families/${record.id}` : `${API_URL}/api/families`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(familyForm)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to save family');
      }
      const saved = await res.json();
      await setFamilyLabels(saved.id, familyLabelIds);
      flash(isEdit ? 'Family updated' : 'Family created');
      onSaved?.(saved, { mode: isEdit ? 'edit' : 'create' });
      onClose();
      onLabelsRefresh?.();
    } catch (err) {
      setFamilyError(err.message);
      flash(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      closeOnBackdrop={closeOnBackdrop}
      closeOnEscape={closeOnEscape}
      title={mode === 'edit' ? 'Edit family' : 'New family'}
      description={
        mode === 'edit' ? 'Update the household details below.' : 'Add a new family to the directory.'
      }
      size="lg"
    >
      <form onSubmit={submit} className={`${styles.form} ${styles.modalEntityForm}`}>
        <section className={styles.modalSection} aria-labelledby={`${formId}-household`}>
          <header className={styles.modalSectionHead}>
            <h4 id={`${formId}-household`} className={styles.modalSectionTitle}>
              Household
            </h4>
            <p className={styles.modalSectionHint}>Case number and primary contact name.</p>
          </header>
          <div className={styles.formGrid}>
            <div className={styles.modalFullRow}>
              <Input
                label="Case number"
                name="case_number"
                required
                value={familyForm.case_number}
                onChange={onFamilyChange}
                disabled={mode === 'edit'}
              />
            </div>
            <Input
              label="First name"
              name="first_name"
              required
              value={familyForm.first_name}
              onChange={onFamilyChange}
            />
            <Input
              label="Last name"
              name="last_name"
              required
              value={familyForm.last_name}
              onChange={onFamilyChange}
            />
          </div>
        </section>

        <section className={styles.modalSection} aria-labelledby={`${formId}-contact`}>
          <header className={styles.modalSectionHead}>
            <h4 id={`${formId}-contact`} className={styles.modalSectionTitle}>
              Contact
            </h4>
            <p className={styles.modalSectionHint}>Reach this family by email or phone.</p>
          </header>
          <div className={styles.formGrid}>
            <Input
              label="Email"
              name="email"
              type="email"
              value={familyForm.email}
              onChange={onFamilyChange}
              iconLeft={<FaEnvelope />}
            />
            <Input
              label="Phone"
              name="phone_number"
              value={familyForm.phone_number}
              onChange={onFamilyChange}
              iconLeft={<FaPhone />}
            />
          </div>
        </section>

        <section className={styles.modalSection} aria-labelledby={`${formId}-addr`}>
          <header className={styles.modalSectionHead}>
            <h4 id={`${formId}-addr`} className={styles.modalSectionTitle}>
              Address
            </h4>
            <p className={styles.modalSectionHint}>Mailing address on file.</p>
          </header>
          <div className={styles.formGrid}>
            <Input
              label="Address line 1"
              name="address_line1"
              value={familyForm.address_line1}
              onChange={onFamilyChange}
              iconLeft={<FaHome />}
            />
            <Input
              label="Address line 2"
              name="address_line2"
              value={familyForm.address_line2}
              onChange={onFamilyChange}
            />
            <Input label="City" name="city" value={familyForm.city} onChange={onFamilyChange} />
            <div className={styles.row}>
              <Input label="State" name="state" value={familyForm.state} onChange={onFamilyChange} />
              <Input label="ZIP" name="zip_code" value={familyForm.zip_code} onChange={onFamilyChange} />
            </div>
          </div>
        </section>

        <section className={styles.modalSection} aria-labelledby={`${formId}-tags`}>
          <header className={styles.modalSectionHead}>
            <h4 id={`${formId}-tags`} className={styles.modalSectionTitle}>
              Labels & status
            </h4>
            <p className={styles.modalSectionHint}>Tags help you filter the directory.</p>
          </header>
          <div className={styles.modalTagBlock}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Labels</span>
              <LabelPicker
                compact
                available={(labels || []).filter((l) => l.kind === 'family' || l.kind === 'both')}
                selectedIds={familyLabelIds}
                onChange={setFamilyLabelIds}
                onCreateLabel={onCreateLabelInline}
              />
            </div>
            <label className={styles.activeToggle}>
              <Toggle
                size="sm"
                checked={!!familyForm.is_active}
                onChange={(v) => setFamilyForm((f) => ({ ...f, is_active: v }))}
                ariaLabel="Toggle active status"
              />
              <span>{familyForm.is_active ? 'Active' : 'Inactive'}</span>
            </label>
          </div>
        </section>

        {familyError ? <div className={styles.errorMsg}>{familyError}</div> : null}
        <div className={styles.formActions}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {mode === 'edit' ? 'Save changes' : 'Create family'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
